import React from 'react'
import { redirect } from 'react-router-dom'

import {
  PerformanceReviewsReviewCrumbDocument,
  PerformanceReviewsReviewParticipantRevieweesCrumbDocument,
  PerformanceReviewsReviewTargetRevieweersCrumbDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildNavigation, buildRoute } from 'modules/core/lib/react-router'

import { getGroupName } from 'modules/reviews/lib/getGroupName'
import questionGroups from 'modules/reviews/store/collections/questionGroups'

const ReviewAnswerPage = React.lazy(
  () => import('modules/reviews/pages/performance/ReviewAnswer/ReviewAnswerPage')
)
const SplitViewReviewAnswerPage = React.lazy(
  () => import('modules/reviews/pages/performance/ReviewAnswer/SplitViewReviewAnswerPage')
)
const RevieweesPage = React.lazy(
  () => import('modules/reviews/pages/performance/Reviewees/RevieweesPage')
)
const ReviewUserIndexPage = React.lazy(
  () => import('modules/reviews/pages/performance/ReviewsReviewee/ReviewUserIndexPage')
)
const ReviewUserResponsePage = React.lazy(
  () => import('modules/reviews/pages/performance/ReviewsReviewee/ReviewUserResponsePage')
)
const ReviewUserRootPage = React.lazy(
  () => import('modules/reviews/pages/performance/ReviewsReviewee/ReviewUserRootPage')
)
const ReviewAnalyticsPage = React.lazy(
  () => import('modules/reviews/pages/performance/Show/Analytics/ReviewAnalyticsPage')
)

const ReviewConfigurationPage = React.lazy(
  () => import('modules/reviews/pages/performance/Show/Configuration/ReviewConfigurationPage')
)

const NavigateToReviewIndexPage = React.lazy(
  () => import('modules/reviews/pages/performance/Show/NavigateToReviewIndexPage')
)

const ParticipantsPage = React.lazy(
  () => import('modules/reviews/pages/performance/Show/Participants/ParticipantsPage')
)
const QuestionsPage = React.lazy(
  () => import('modules/reviews/pages/performance/Show/Questions/QuestionsPage')
)
const ResponsesPage = React.lazy(
  () => import('modules/reviews/pages/performance/Show/Responses/ResponsesPage')
)
const ReviewsTabPage = React.lazy(
  () => import('modules/reviews/pages/performance/Show/Reviews/ReviewsTabPage')
)
const WriteReviewsPage = React.lazy(
  () => import('modules/reviews/pages/performance/Show/WriteReviews/WriteReviewsPage')
)

const RootPage = React.lazy(() => import('modules/reviews/pages/RootPage'))

const ReviewsIndexPage = React.lazy(
  () => import('modules/reviews/pages/performance/Index/ReviewsIndexPage')
)

const ReviewShowPage = React.lazy(
  () => import('modules/reviews/pages/performance/Show/ReviewShowPage')
)

const performanceRoutes = buildNavigation((navigation) => {
  return [
    buildRoute({
      path: navigation.performance.reviews.path,
      element: <RootPage />,
      handle: {
        meta: () => {
          return {
            title: i18n.t('surveys.performance.index.title'),
            description: i18n.t('surveys.performance.index.description'),
            illustration: 'surveys',
          }
        },
      },
      children: [
        {
          index: true,
          element: <ReviewsIndexPage />,
        },
        {
          path: navigation.performance.reviews.id.path,
          handle: {
            resource: true,
            meta: ({ watchQuery, params }) => {
              if (!params.id) return { title: '', description: '' }
              return watchQuery({
                query: PerformanceReviewsReviewCrumbDocument,
                variables: {
                  id: Number(params.id),
                },
              }).map(
                ({
                  data: {
                    performanceReviews: { review },
                  },
                }) => ({
                  title: `${review?.name ?? i18n.t('performance_process.reviews.untitled')}`,
                })
              )
            },
          },
          element: <ReviewShowPage />,
          children: [
            {
              index: true,
              element: <NavigateToReviewIndexPage />,
            },
            {
              path: navigation.performance.reviews.id.configuration.path,
              element: <ReviewConfigurationPage />,
              handle: {
                resource: true,
                meta: () => {
                  return {
                    title: i18n.t('surveys.form.tabs.index'),
                  }
                },
              },
            },
            {
              path: navigation.performance.reviews.id.questions.path,
              element: <QuestionsPage />,
              handle: {
                resource: true,
                meta: () => {
                  return {
                    title: i18n.t('surveys.form.tabs.questions'),
                  }
                },
              },
            },
            {
              path: navigation.performance.reviews.id.participants.path,
              handle: {
                resource: true,
                meta: () => {
                  return {
                    title: i18n.t('surveys.form.tabs.participants'),
                  }
                },
              },
              children: [
                {
                  index: true,
                  element: <ParticipantsPage />,
                  handle: {
                    resource: true,
                  },
                },
                {
                  path: navigation.performance.reviews.id.participants.revieweesUser.path,
                  element: <RevieweesPage />,
                  handle: {
                    resource: true,
                    meta: ({ watchQuery, params }) => {
                      if (!params.user_id) return { description: '', title: '' }
                      return watchQuery({
                        query: PerformanceReviewsReviewParticipantRevieweesCrumbDocument,
                        variables: {
                          id: Number(params.user_id),
                        },
                      }).map(
                        ({
                          data: {
                            apiCore: { access },
                          },
                        }) => ({
                          description: i18n.t('surveys.review.participants.reviewee.description'),
                          title: i18n.t('surveys.review.participants.reviewee.title', {
                            user: access?.employee?.preferredName ?? access?.employee?.firstName,
                          }),
                        })
                      )
                    },
                  },
                },
              ],
            },
            {
              path: navigation.performance.reviews.id.reviews.path,
              handle: {
                resource: true,
                meta: () => {
                  return {
                    title: i18n.t('surveys.form.tabs.reviews'),
                  }
                },
              },
              children: [
                {
                  index: true,
                  element: <ReviewsTabPage />,
                  handle: {
                    resource: true,
                  },
                },
                {
                  path: navigation.performance.reviews.id.reviews.targetReviewers.path,
                  element: <ReviewUserRootPage />,
                  handle: {
                    resource: true,
                    meta: ({ watchQuery, params }) => {
                      if (!params.user_id) return { description: '', title: '' }
                      return watchQuery({
                        query: PerformanceReviewsReviewTargetRevieweersCrumbDocument,
                        variables: {
                          id: Number(params.user_id),
                        },
                      }).map(
                        ({
                          data: {
                            apiCore: { access },
                          },
                        }) => ({
                          description: i18n.t('surveys.review.reviews.reviewee.description'),
                          title: i18n.t('surveys.review.reviews.reviewee.title', {
                            user: access?.employee?.preferredName ?? access?.employee?.firstName,
                          }),
                        })
                      )
                    },
                  },
                  children: [
                    {
                      index: true,
                      element: <ReviewUserIndexPage />,
                      handle: {
                        resource: true,
                      },
                    },
                    {
                      path: navigation.performance.reviews.id.reviews.targetReviewers
                        .responseQuestionGroupId.path,
                      element: <ReviewUserResponsePage />,
                      loader: ({ params }) => {
                        return questionGroups.fetch({
                          data: {
                            surveys_form_id: Number(params.id),
                          },
                        })
                      },
                      handle: {
                        resource: true,
                        meta: ({ params }) => {
                          const questionGroup = questionGroups.get(Number(params.question_group_id))
                          return {
                            title: getGroupName(questionGroup?.respondentGroup) ?? '',
                          }
                        },
                      },
                    },
                    {
                      path: navigation.performance.reviews.id.reviews.targetReviewers.splitView
                        .path,
                      element: <SplitViewReviewAnswerPage />,
                      handle: {
                        resource: true,
                        meta: () => {
                          return {
                            title: i18n.t('surveys.review.reviews.split_view.title'),
                            illustration: 'user-interaction-review_chart',
                            description: i18n.t('surveys.review.reviews.split_view.description'),
                          }
                        },
                      },
                    },
                  ],
                },
              ],
            },
            {
              path: navigation.performance.reviews.id.writeReviews.path,
              handle: {
                resource: true,
                meta: () => {
                  return {
                    title: i18n.t('surveys.form.tabs.write_reviews'),
                  }
                },
              },
              children: [
                {
                  index: true,
                  element: <WriteReviewsPage />,
                  handle: {
                    resource: true,
                  },
                },
                {
                  path: navigation.performance.reviews.id.writeReviews.answerQuestionGroupId.path,
                  element: <ReviewAnswerPage />,
                  loader: ({ params }) => {
                    return questionGroups.fetch({
                      data: {
                        surveys_form_id: Number(params.id),
                      },
                    })
                  },
                  handle: {
                    resource: true,
                    meta: ({ params }) => {
                      const questionGroup = questionGroups.get(Number(params.question_group_id))
                      return { title: getGroupName(questionGroup?.respondentGroup) ?? '' }
                    },
                  },
                },
                {
                  path: navigation.performance.reviews.id.writeReviews.responsesQuestionGroupId
                    .path,
                  element: <ResponsesPage />,
                  loader: ({ params }) => {
                    return questionGroups.fetch({
                      data: {
                        surveys_form_id: Number(params.id),
                      },
                    })
                  },
                  handle: {
                    resource: true,
                    meta: ({ params }) => {
                      const questionGroup = questionGroups.get(Number(params.question_group_id))
                      return { title: getGroupName(questionGroup?.respondentGroup) ?? '' }
                    },
                  },
                },
              ],
            },
            {
              path: navigation.performance.reviews.id.analytics.path,
              element: <ReviewAnalyticsPage />,
              handle: {
                resource: true,
                meta: () => {
                  return {
                    title: i18n.t('surveys.form.tabs.analytics'),
                  }
                },
              },
            },
          ],
        },
        {
          path: navigation.performance.reviews.idAnswerQuestionGroupId.path,
          // TODO: delete this redirect after sometime
          loader: ({ params }) => {
            if (!params.id || !params.question_group_id) {
              return null
            }
            return redirect(
              navigation.performance.reviews.id.writeReviews.answerQuestionGroupId.fullPath({
                id: params.id,
                question_group_id: params.question_group_id,
              })
            )
          },
        },
        {
          path: navigation.performance.reviews.splitViewidAnswerQuestionGroupId.path,
          element: <SplitViewReviewAnswerPage />,
          // TODO: delete this redirect after sometime
          loader: ({ params }) => {
            if (!params.id || !params.user_id) {
              return null
            }
            return redirect(
              navigation.performance.reviews.id.reviews.targetReviewers.splitView.fullPath({
                id: params.id,
                user_id: params.user_id,
              })
            )
          },
        },
        {
          path: navigation.performance.reviews.idResponsesQuestionGroupId.path,
          // NOTE: This redirect shouldn't be deleted until we found the entrypoint for this route
          loader: ({ params }) => {
            if (!params.id || !params.question_group_id) {
              return null
            }
            return redirect(
              navigation.performance.reviews.id.writeReviews.responsesQuestionGroupId.fullPath({
                id: params.id,
                question_group_id: params.question_group_id,
              })
            )
          },
          handle: {
            resource: true,
            meta: ({ params }) => {
              const questionGroup = questionGroups.get(Number(params.question_group_id))
              return {
                title: getGroupName(questionGroup?.respondentGroup) ?? '',
              }
            },
          },
        },
      ],
    }),
  ]
})

export default performanceRoutes
